import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ThemeContext } from "../contexts/ThemeContext";
import { NavbarContext } from "../contexts/NavbarContext";
import { getProjectBySlug } from "../../data/projectsData";
import { CONTACT, RDV, REALISATIONS, SERVICES, realisationDetail } from "../../config/routes";
import "./styles/ProjectDetail.css";

const STATUS_LABELS = {
  production: "En production",
  mvp: "MVP",
  "in-progress": "En cours",
  prototype: "Prototype",
  "client-project": "Projet client",
};

function cn(...x) {
  return x.filter(Boolean).join(" ");
}

function CaseStudyList({ title, items }) {
  if (!items?.length) return null;
  return (
    <div className="caseStudyBlock">
      <h3>{title}</h3>
      <ul className="list">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } =
    useContext(NavbarContext);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 599 : false
  );

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const project = getProjectBySlug(slug);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();

    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, {
        passive: false,
      });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }

    return () => document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".project");
    if (!mainContainer) return;

    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  const galleryItems = useMemo(() => {
    const raw = (project?.gallery || []).slice(0, 12);
    return raw
      .map((item) => (typeof item === "string" ? { src: item, format: "auto" } : item))
      .filter((x) => x?.src && !String(x.src).startsWith("TODO"));
  }, [project]);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const goPrev = useCallback(() => {
    setLightboxIndex((i) => (i - 1 + galleryItems.length) % galleryItems.length);
  }, [galleryItems.length]);

  const goNext = useCallback(() => {
    setLightboxIndex((i) => (i + 1) % galleryItems.length);
  }, [galleryItems.length]);

  useEffect(() => {
    if (!lightboxOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [lightboxOpen, closeLightbox, goPrev, goNext]);

  const siteUrl = "https://dreameragency.fr";

  const canonical = project
    ? `${siteUrl}${realisationDetail(project.slug)}`
    : `${siteUrl}${REALISATIONS}`;

  const metaTitle = project
    ? `${project.title} - Réalisation | Dreamer Agency`
    : "Projet introuvable - Dreamer Agency";

  const metaDesc =
    (project?.summary || "").trim() ||
    (project?.technicalSummary || "").trim() ||
    (project?.subtitle || "").trim() ||
    "Découvrez une réalisation Dreamer Agency (produit web, SaaS ou application mobile).";

  const firstGalleryImage = project?.gallery?.find(
    (item) =>
      item &&
      typeof item === "object" &&
      item.src &&
      !String(item.src).startsWith("TODO")
  );
  const ogImage = firstGalleryImage?.src?.startsWith("http")
    ? firstGalleryImage.src
    : firstGalleryImage?.src
      ? `${siteUrl}${firstGalleryImage.src}`
      : "https://dreameragency.fr/media/OG/agency_background.png";

  const caseStudy = project?.caseStudy;
  const isShortCase = !caseStudy;
  const projectStatus = project?.status ? STATUS_LABELS[project.status] || project.status : null;
  const roleItems = project?.role || [];
  const highlightItems = project?.highlights || [];
  const stackItems = project?.stack || [];
  const infraItems = project?.infra || [];
  const hasProjectLinks =
    !!project?.links?.website ||
    (!!project?.links?.appStore && project.links.appStore !== "TODO_APPSTORE_URL") ||
    (!!project?.links?.playStore && project.links.playStore !== "TODO_PLAYSTORE_URL");

  const jsonLd = !project
    ? []
    : [
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Dreamer Agency",
          url: siteUrl,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Réalisations",
              item: `${siteUrl}${REALISATIONS}`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: project.title,
              item: canonical,
            },
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          name: project.title,
          description: metaDesc,
          url: canonical,
          provider: {
            "@type": "Organization",
            name: "Dreamer Agency",
          },
        },
      ];

  if (!project) {
    return (
      <main
        className={cn("project", darkMode ? "dark" : "light")}
        onClick={handleMainContainerClick}
      >
        <Helmet>
          <title>{metaTitle}</title>
          <meta
            name="description"
            content="Ce projet n’existe pas (ou a été déplacé)."
          />
          <link rel="canonical" href={`${siteUrl}${REALISATIONS}`} />
          <meta name="robots" content="noindex,follow" />
          <meta property="og:title" content={metaTitle} />
          <meta
            property="og:description"
            content="Ce projet n’existe pas (ou a été déplacé)."
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`${siteUrl}${REALISATIONS}`} />
          <meta property="og:image" content={ogImage} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={metaTitle} />
          <meta
            name="twitter:description"
            content="Ce projet n’existe pas (ou a été déplacé)."
          />
          <meta name="twitter:image" content={ogImage} />
        </Helmet>

        <section className="project-hero">
          <div className="project-container">
            <p className="project-eyebrow">Réalisations</p>
            <h1 className="project-h1">Projet introuvable</h1>
            <p className="project-lead">
              Ce lien ne correspond à aucun projet. Retourne à la liste :
            </p>
            <div className="project-actions">
              <Link to={REALISATIONS} className="btn btn-primary">
                Voir les réalisations
              </Link>
              <Link to={RDV} className="btn btn-secondary">
                Réserver un appel
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main
      className={cn("project", darkMode ? "dark" : "light")}
      onClick={handleMainContainerClick}
    >
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonical} />

        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="Dreamer Agency" />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <section className="project-hero">
        <div className="project-container">
          <p className="project-eyebrow">
            <Link className="project-back" to={REALISATIONS}>
              ← Réalisations
            </Link>
          </p>

          <div className="project-heroGrid">
            <div>
              <div className="project-tags">
                {(project.tags || []).slice(0, 4).map((t) => (
                  <span className="tag" key={`${project.slug}-${t}`}>
                    {t}
                  </span>
                ))}
              </div>

              <h1 className="project-h1">{project.title}</h1>
              <p className="project-lead">{project.subtitle}</p>

              <div className="project-actions">
                <Link to={RDV} className="btn btn-primary">
                  Réserver un appel
                </Link>
                <Link to={SERVICES} className="btn btn-secondary">
                  Voir les services
                </Link>
                <Link to={CONTACT} className="btn btn-ghost">
                  Contact
                </Link>
              </div>
            </div>

            <aside className="project-metaCard">
              <div className="project-metaRow">
                <span className="project-metaLabel">Année</span>
                <span className="project-metaValue">
                  {project.meta?.year || "-"}
                </span>
              </div>
              <div className="project-metaRow">
                <span className="project-metaLabel">Durée</span>
                <span className="project-metaValue">
                  {project.meta?.duration || "-"}
                </span>
              </div>
              <div className="project-metaRow">
                <span className="project-metaLabel">Production</span>
                <span className="project-metaValue">
                  {project.meta?.deliveryMode || "-"}
                </span>
              </div>
              <div className="project-metaRow">
                <span className="project-metaLabel">Disponibilité</span>
                <span className="project-metaValue">
                  {project.meta?.availability || "-"}
                </span>
              </div>

              {project.type ? (
                <div className="project-metaRow">
                  <span className="project-metaLabel">Type</span>
                  <span className="project-metaValue">{project.type}</span>
                </div>
              ) : null}

              {projectStatus ? (
                <div className="project-metaRow">
                  <span className="project-metaLabel">Statut</span>
                  <span className="project-metaValue">{projectStatus}</span>
                </div>
              ) : null}

              {project.meta?.confidentiality ? (
                <div className="project-metaNote">
                  <span className="project-metaNoteTitle">Info</span>
                  <p className="project-metaNoteText">
                    {project.meta.confidentiality}
                  </p>
                </div>
              ) : null}
            </aside>
          </div>
        </div>
      </section>

      <section className="project-section">
        <div className="project-container project-grid2">
          <div className="project-col">
            {caseStudy ? (
              <div className="caseStudy">
                <div className="panel caseStudyIntro">
                  <p className="project-sectionEyebrow">Étude de cas V1</p>
                  <h2 className="project-h2">Résumé projet</h2>
                  <p className="muted">
                    {project.summary || project.businessSummary || project.subtitle}
                  </p>
                  {project.technicalSummary ? (
                    <p className="muted small">{project.technicalSummary}</p>
                  ) : null}
                </div>

                <div className="caseStudyNarrative">
                  <div className="panel">
                    <h2 className="project-h2">Contexte</h2>
                    <p className="muted">{caseStudy.context}</p>
                  </div>

                  <div className="panel">
                    <h2 className="project-h2">Problème</h2>
                    <p className="muted">{caseStudy.problem}</p>
                  </div>

                  <div className="panel">
                    <h2 className="project-h2">Solution</h2>
                    <p className="muted">{caseStudy.solution}</p>
                  </div>

                  <div className="panel">
                    <h2 className="project-h2">Rôle de Florian</h2>
                    <p className="muted">{caseStudy.role}</p>
                  </div>
                </div>

                <div className="caseStudyGrid">
                  <CaseStudyList
                    title="Fonctionnalités principales"
                    items={caseStudy.keyFeatures}
                  />
                  <CaseStudyList
                    title="Choix techniques"
                    items={caseStudy.technicalChoices}
                  />
                  <CaseStudyList
                    title="Enjeux / difficultés"
                    items={caseStudy.challenges}
                  />
                  <CaseStudyList
                    title="Ce que ça démontre pour un recruteur"
                    items={caseStudy.recruiterTakeaways}
                  />
                  <CaseStudyList
                    title="Ce que ça démontre pour un client"
                    items={caseStudy.clientTakeaways}
                  />
                </div>

                {caseStudy.statusNote ? (
                  <div className="panel caseStudyStatus">
                    <h2 className="project-h2">Statut</h2>
                    <p className="muted">{caseStudy.statusNote}</p>
                  </div>
                ) : null}
              </div>
            ) : null}

            {isShortCase ? (
              <div className="panel">
                <h2 className="project-h2">Fiche projet courte</h2>
                <p className="muted">
                  {project.summary || project.businessSummary || project.subtitle}
                </p>
                {project.businessSummary ? (
                  <p className="muted small">{project.businessSummary}</p>
                ) : null}
                {project.technicalSummary ? (
                  <p className="muted small">{project.technicalSummary}</p>
                ) : null}
              </div>
            ) : null}

            {project.sensitiveNote ? (
              <div className="panel panelNotice">
                <h2 className="project-h2">Note de contexte</h2>
                <p className="muted">{project.sensitiveNote}</p>
              </div>
            ) : null}

            {roleItems.length ? (
              <div className="panel">
                <h2 className="project-h2">Ce qu’on a livré</h2>
                <ul className="list">
                  {roleItems.map((x) => (
                    <li key={`${project.slug}-role-${x}`}>{x}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {highlightItems.length ? (
              <div className="panel">
                <h2 className="project-h2">Points clés</h2>
                <ul className="list">
                  {highlightItems.map((x) => (
                    <li key={`${project.slug}-hl-${x}`}>{x}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {stackItems.length ? (
              <div className="panel">
                <h2 className="project-h2">Stack</h2>
                <div className="chips">
                  {stackItems.map((s) => (
                    <span className="chip" key={`${project.slug}-stack-${s}`}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {infraItems.length ? (
              <div className="panel">
                <h2 className="project-h2">Infra & hébergement</h2>
                <ul className="list">
                  {infraItems.map((x) => (
                    <li key={`${project.slug}-infra-${x}`}>{x}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {hasProjectLinks ? (
            <div className="panel">
              <h2 className="project-h2">Liens</h2>

              <div className="links">
                {project.links?.website && (
                  <a
                    className="linkRow"
                    href={project.links.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Site / Démo <span className="arrow">→</span>
                  </a>
                )}

                {project.links?.appStore &&
                project.links.appStore !== "TODO_APPSTORE_URL" && (
                  <a
                    className="linkRow"
                    href={project.links.appStore}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    App Store <span className="arrow">→</span>
                  </a>
                )}

                {project.links?.playStore &&
                project.links.playStore !== "TODO_PLAYSTORE_URL" && (
                  <a
                    className="linkRow"
                    href={project.links.playStore}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Play Store <span className="arrow">→</span>
                  </a>
                )}
              </div>

              <div className="project-miniCta">
                <Link to={RDV} className="btn btn-tertiary">
                  Projet similaire ? On en parle →
                </Link>
              </div>
            </div>
            ) : null}
          </div>

          <aside className="project-col">
            {galleryItems.length ? (
            <div className="panel">
              <h2 className="project-h2">Aperçu</h2>

              <div className="gallery">
                {galleryItems.slice(0, 6).map((obj, i) => {
                  const format = obj?.format || "auto";
                  const klass = cn(
                    "galleryItem",
                    format === "mobile" && "galleryItem--mobile",
                    format === "desktop" && "galleryItem--desktop",
                    format === "square" && "galleryItem--square"
                  );

                  return (
                    <div className={klass} key={`${project.slug}-img-${i}`}>
                      <button
                        type="button"
                        className="galleryBtn"
                        onClick={() => openLightbox(i)}
                        aria-label={`Ouvrir ${project.title} - capture ${i + 1}`}
                      >
                        <img
                          src={obj.src}
                          alt={`${project.title} - capture ${i + 1}`}
                          loading="lazy"
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            ) : null}

            {lightboxOpen && galleryItems.length > 0 ? (
              <div className="lightbox" role="dialog" aria-modal="true">
                <div className="lightboxOverlay" onClick={closeLightbox} />

                <div className="lightboxContent" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="lightboxClose"
                    onClick={closeLightbox}
                    aria-label="Fermer"
                  >
                    ✕
                  </button>

                  {galleryItems.length > 1 ? (
                    <>
                      <button
                        type="button"
                        className="lightboxNav lightboxNav--left"
                        onClick={goPrev}
                        aria-label="Image précédente"
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        className="lightboxNav lightboxNav--right"
                        onClick={goNext}
                        aria-label="Image suivante"
                      >
                        →
                      </button>
                    </>
                  ) : null}

                  <img
                    className="lightboxImg"
                    src={galleryItems[lightboxIndex]?.src}
                    alt={`${project.title} - capture ${lightboxIndex + 1}`}
                  />

                  {galleryItems.length > 1 ? (
                    <div className="lightboxCounter">
                      {lightboxIndex + 1} / {galleryItems.length}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="panel panelGradient">
              <h2 className="project-h2">Tu veux le même niveau de qualité ?</h2>
              <p className="muted">
                On te propose un scope MVP clair, une architecture propre, et une
                livraison stable (web / SaaS / mobile).
              </p>
              <div className="project-actions">
                <Link to={RDV} className="btn btn-primary">
                  Réserver un appel
                </Link>
                <Link to={CONTACT} className="btn btn-ghost">
                  Nous écrire
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {jsonLd.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </main>
  );
}
